import React, { useMemo } from "react";

import COLORS from "@appsinti/stats-frontend/resources/constants/colors";
import IChartData from "@appsinti/stats-frontend/interfaces/IChartData";
import { CustomCard, LegendContainer, Label, ReferenceContainer } from "../styles";
import Header from "../components/Header";
import Loader from "../components/Loader";

import useI18n from "@appsinti/i18n/useI18n";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
} from "chart.js";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";
import type { ChartData, ScriptableLineSegmentContext } from "chart.js";
import "chartjs-adapter-date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
  zoomPlugin,
  annotationPlugin
);

import { LineOptions, getOptions } from "./options";
import { Checkbox } from "@etiquette-ui/selectors";
import styled from "styled-components";
import { formatMoneyMK } from "@appsinti/common-utils/textFormat";

const CustomCheckbox = styled(Checkbox)`
  justify-content: space-between;

  input {
    width: 17px;
    height: 17px;
    border-width: 2px;
    margin: 0;

    &:checked:before {
      top: 2px;
      left: 4px;
    }
  }
`;

type DottedSegmentOptions = {
  hasADottedSegment?: boolean;
  dottedSegmentType?: "complete" | "segment";
  dottedSegmentAlignment?: "start" | "end";
};

type LineChart = {
  dataObject: IChartData | null;
  isLoading?: boolean;
  showLegend?: boolean;
};

const shouldBeDotted = (
  dottedSegmentOptions: DottedSegmentOptions,
  isCurrent: boolean | undefined,
  nonNullArray: number[],
  ctx: ScriptableLineSegmentContext
) => {
  const lastValidIndex = nonNullArray[nonNullArray.length - 1];

  if (
    dottedSegmentOptions.hasADottedSegment &&
    dottedSegmentOptions.dottedSegmentType === "complete" &&
    isCurrent
  )
    return true;
  if (
    dottedSegmentOptions.hasADottedSegment &&
    dottedSegmentOptions.dottedSegmentType === "segment" &&
    isCurrent
  ) {
    const firstValidIndex = nonNullArray[0];
    if (
      dottedSegmentOptions.dottedSegmentAlignment === "start" &&
      ctx.p0DataIndex === firstValidIndex
    )
      return true;
    if (
      dottedSegmentOptions.dottedSegmentAlignment === "end" &&
      (ctx.p1DataIndex === lastValidIndex || ctx.p0DataIndex === lastValidIndex)
    )
      return true;
  }
  return false;
};

const borderDashCallback =
  (
    dottedSegmentOptions: DottedSegmentOptions,
    isCurrent: boolean | undefined,
    nonNullArray: number[]
  ) =>
    (ctx: ScriptableLineSegmentContext): number[] | undefined => {
      const dotted = shouldBeDotted(
        dottedSegmentOptions,
        isCurrent,
        nonNullArray,
        ctx
      );

      return dotted ? [2, 5] : undefined;
    };

const borderCapStyleCallback =
  (
    dottedSegmentOptions: DottedSegmentOptions,
    isCurrent: boolean | undefined,
    nonNullArray: number[]
  ) =>
    (ctx: ScriptableLineSegmentContext): "round" | undefined => {
      const dotted = shouldBeDotted(
        dottedSegmentOptions,
        isCurrent,
        nonNullArray,
        ctx
      );

      return dotted ? "round" : undefined;
    };

const borderWidthCallback =
  (
    dottedSegmentOptions: DottedSegmentOptions,
    isCurrent: boolean | undefined,
    nonNullArray: number[]
  ) =>
    (ctx: ScriptableLineSegmentContext): number | undefined => {
      const dotted = shouldBeDotted(
        dottedSegmentOptions,
        isCurrent,
        nonNullArray,
        ctx
      );

      return dotted ? 2.3 : undefined;
    };

const LineChart: React.FC<LineChart> = ({
  dataObject,
  isLoading = false,
  showLegend = true,
}) => {
  const { translateObject, translate } = useI18n();

  const isCurrency = dataObject?.isCurrency ?? true;

  const formatAmount = (amount: number) =>
    formatMoneyMK(amount, isCurrency ? undefined : { currencySymbol: "" });

  const computedDottedSegmentOptions = useMemo(() => {
    if (!dataObject) return { hasADottedSegment: false };

    if (dataObject.dottedType) {
      switch (dataObject.dottedType) {
        case "complete":
          return {
            hasADottedSegment: true,
            dottedSegmentType: "complete",
          } as DottedSegmentOptions;
        case "segment-start":
          return {
            hasADottedSegment: true,
            dottedSegmentType: "segment",
            dottedSegmentAlignment: "start",
          } as DottedSegmentOptions;
        case "segment-end":
          return {
            hasADottedSegment: true,
            dottedSegmentType: "segment",
            dottedSegmentAlignment: "end",
          } as DottedSegmentOptions;
        default:
          return { hasADottedSegment: false };
      }
    }

    return { hasADottedSegment: false };
  }, [dataObject]);

  const computedLineOptions = useMemo(() => {
    if (!dataObject) return {};

    return {
      isTimeBased: dataObject.isTimeBased,
      isCurrency,
      isPercentageSet: dataObject.isPercentageData ?? false,
      xAxis: {
        hasTextAsValue: true,
        min: dataObject.xAxis?.min,
        max: dataObject.xAxis?.max,
        reverse: dataObject.xAxis?.reverse,
      },
      yAxis: {
        hasTextAsValue: false,
        min: dataObject.yAxis?.min,
        max: dataObject.yAxis?.max,
      },
      timeOptions: dataObject.timeOptions || {
        unit: "day",
        displayFormats: { day: "EEEEEE" },
      },
      annotations: dataObject.references?.map(reference => ({
        key: reference.key,
        title: `${translateObject(reference.title)} ( ${formatAmount(reference.amount)} )`,
        label: formatAmount(reference.amount),
        amount: reference.amount,
        xAxisLimits: reference.xAxisLimits,
        yAxisLimits: {
          min: reference.amount,
          max: reference.amount,
        }
      })) || [],
    };
  }, [dataObject, translateObject, isCurrency]);

  const [annotationVisibility, setAnnotationVisibility] = React.useState(
    computedLineOptions.annotations?.reduce(
      (acc, annotation) => {
        acc[annotation.key] = true;
        return acc;
      }
      , {} as { [key: string]: boolean; }) ?? {});

  const customLabels =
    dataObject?.isTimeBased && dataObject?.realLabels && dataObject?.labels
      ? dataObject.labels.reduce((acc, labelDate, index) => {
        acc[`${labelDate}`] = translateObject(dataObject.realLabels[index]);
        return acc;
      }, {} as { [key: number]: any; })
      : {};

  const options = useMemo(
    () => getOptions(computedLineOptions, customLabels, annotationVisibility),
    [computedLineOptions, customLabels, annotationVisibility]
  );

  const changeAnnotationVisibility = (key: string) => {
    setAnnotationVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const data: ChartData<"line", number[], string | number> | null = dataObject
    ? {
      labels: dataObject.isTimeBased
        ? dataObject.labels
        : dataObject.labels.map((label) => {
          if (typeof label === "string" || typeof label === "undefined") {
            return label ?? "-";
          }

          return translateObject(label);
        }),
      datasets: dataObject.datasets.map((dataset, index, datasets) => {
        const { data, label, isProjection, isCurrent } = dataset;
        const defaultColor =
          COLORS[
          (COLORS.length - ((datasets.length - index) % COLORS.length)) %
          COLORS.length
          ];
        const PROJECTION_COLOR = "#888888";
        const borderColor = isProjection ? PROJECTION_COLOR : defaultColor;

        const borderDash = isProjection ? [5, 5] : [];

        const nonNullIndex = data
          .map((value, index) => (value !== null ? index : null))
          .filter((value): value is number => value !== null);

        const segment = isProjection
          ? {}
          : {
            borderDash: borderDashCallback(
              computedDottedSegmentOptions,
              isCurrent,
              nonNullIndex
            ),
            borderCapStyle: borderCapStyleCallback(
              computedDottedSegmentOptions,
              isCurrent,
              nonNullIndex
            ),
            borderWidth: borderWidthCallback(
              computedDottedSegmentOptions,
              isCurrent,
              nonNullIndex
            ),
          };

        return {
          data: data,
          label:
            typeof label === "string" || typeof label === "undefined"
              ? label ?? "-"
              : translateObject(label),
          backgroundColor: "#FFF",
          fill: true,
          borderColor,
          borderWidth: 1.4,
          borderDash,
          segment
        };
      }),
    }
    : null;

  return (
    <CustomCard
      background="white"
      header={
        <Header
          title={
            isLoading === false && !!dataObject
              ? translateObject(dataObject.title)
              : translate("Loading...")
          }
          subtitle={
            !!dataObject && dataObject.subtitle
              ? translateObject(dataObject.subtitle)
              : ""
          }
          to={dataObject?.editLocation}
        />
      }
      body={
        isLoading === false && !!data && !!data.datasets ? (
          <>
            <Line options={options} data={data} height={250} />
            {showLegend && (
              <LegendContainer>
                {data.datasets.map(
                  (dataset, index) =>
                    !!dataset.label && (
                      <Label
                        key={dataset.label}
                        color={dataset.borderColor?.toString() ?? ""}
                      >
                        {dataset.label}
                      </Label>
                    )
                )}
              </LegendContainer>
            )}
            {computedLineOptions.annotations && computedLineOptions.annotations.length > 0 && (
              <ReferenceContainer style={{ marginTop: "10px" }}>
                <h3>Reference lines:</h3>
                {computedLineOptions.annotations.map((annotation, idx) => (
                  <div style={{ display: "flex", justifyContent: "center", flex: 1 }} onClick={() => changeAnnotationVisibility(annotation.key)}>
                    <div style={{ display: "flex", flex: 0.8, justifyContent: "space-between", alignItems: "center", maxWidth: "80%" }}>
                      <Label
                        className="line-indicator"
                        color={COLORS[COLORS.length - 1 - (idx % COLORS.length)].toString()}
                      >
                        {annotation.title}
                      </Label>
                      <CustomCheckbox checked={annotationVisibility[annotation.key]} className={''} disabled={false} color={COLORS[COLORS.length - 1 - (idx % COLORS.length)]} inputProps={{ onClick: () => { } }}>{null}</CustomCheckbox>
                    </div>
                  </div>
                ))}
              </ReferenceContainer>
            )}
          </>
        ) : (
          <Loader />
        )
      }
    />
  );
};
export default LineChart;
